import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Header } from "./IndustriesPanel";

export default function UsersPanel() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password.trim()) {
      toast.error("Email and password are required.");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/users", form);
      setForm({ email: "", name: "", password: "" });
      await load();
      toast.success("User created.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const setActive = async (id, value) => {
    try {
      await api.patch(`/admin/users/${id}`, { is_active: value });
      await load();
      toast.success(value ? "User activated." : "User deactivated.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetPassword = async (id) => {
    const pw = window.prompt("Set a new password (min 8 chars):");
    if (!pw) return;
    if (pw.length < 8) {
      toast.error("Password too short.");
      return;
    }
    try {
      await api.patch(`/admin/users/${id}`, { password: pw });
      toast.success("Password updated.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const renameUser = async (id, newName) => {
    try {
      await api.patch(`/admin/users/${id}`, { name: newName });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      await load();
      toast.success("User deleted.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="users-panel" className="space-y-6">
      <Header
        title="Users"
        subtitle="Admin accounts that can sign into this CMS."
      />

      <form
        onSubmit={create}
        data-testid="user-add-form"
        className="bg-white border border-neutral-200 rounded-xl p-5 grid grid-cols-1 md:grid-cols-12 gap-3"
      >
        <div className="md:col-span-4">
          <Label>Email</Label>
          <Input
            data-testid="user-input-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="ops@kyro.ae"
            autoComplete="off"
          />
        </div>
        <div className="md:col-span-3">
          <Label>Name (optional)</Label>
          <Input
            data-testid="user-input-name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Ops Lead"
          />
        </div>
        <div className="md:col-span-3">
          <Label>Password</Label>
          <Input
            data-testid="user-input-password"
            type="password"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            placeholder="Min 8 characters"
            autoComplete="new-password"
          />
        </div>
        <div className="md:col-span-2 flex items-end">
          <Button
            type="submit"
            data-testid="user-add"
            disabled={creating}
            className="w-full bg-brand text-white hover:bg-brand-dark font-semibold"
          >
            {creating ? "Adding…" : "Add user"}
          </Button>
        </div>
      </form>

      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        {loading && <div className="p-4 text-sm text-neutral-500">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="p-6 text-sm text-neutral-500">No users yet.</div>
        )}
        <ul className="divide-y divide-neutral-100">
          {items.map((u) => (
            <li
              key={u.id}
              data-testid={`user-row-${u.email}`}
              className="px-4 py-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-center"
            >
              <div className="md:col-span-4">
                <div className="font-display font-bold text-neutral-950 truncate">
                  {u.email}
                  {u.id === me?.id && (
                    <span className="ml-2 font-mono-ui text-[9px] uppercase tracking-[0.2em] text-neutral-500">
                      you
                    </span>
                  )}
                </div>
                <div className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500 mt-0.5">
                  {(u.created_at || "").slice(0, 10)} · {u.role || "admin"}
                </div>
              </div>
              <div className="md:col-span-3">
                <Input
                  data-testid={`user-name-${u.email}`}
                  defaultValue={u.name || ""}
                  onBlur={(e) => {
                    if (e.target.value !== (u.name || "")) renameUser(u.id, e.target.value);
                  }}
                  placeholder="Display name"
                />
              </div>
              <div className="md:col-span-2">
                <label
                  className={`inline-flex items-center gap-2 cursor-pointer select-none ${
                    u.id === me?.id ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  title={u.id === me?.id ? "You can't deactivate yourself" : ""}
                >
                  <input
                    type="checkbox"
                    data-testid={`user-active-${u.email}`}
                    checked={Boolean(u.is_active)}
                    disabled={u.id === me?.id}
                    onChange={(e) => setActive(u.id, e.target.checked)}
                    className="w-4 h-4 accent-brand"
                  />
                  <span className="text-xs font-medium text-neutral-700">
                    {u.is_active ? "Active" : "Inactive"}
                  </span>
                </label>
              </div>
              <div className="md:col-span-3 flex justify-end gap-2">
                <button
                  type="button"
                  data-testid={`user-reset-${u.email}`}
                  onClick={() => resetPassword(u.id)}
                  className="text-xs font-semibold text-neutral-700 hover:text-neutral-900 px-2 py-1 rounded-md border border-neutral-200 hover:bg-neutral-50"
                >
                  Reset password
                </button>
                {u.id !== me?.id && (
                  <button
                    type="button"
                    data-testid={`user-delete-${u.email}`}
                    onClick={() => remove(u.id, u.email)}
                    className="text-xs font-semibold text-red-600 hover:text-red-700 px-2 py-1 rounded-md hover:bg-red-50"
                  >
                    Delete
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const Label = ({ children }) => (
  <span className="block font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">
    {children}
  </span>
);
